
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function About() {
  const navigate = useNavigate();
  
  const team = [
    { name: 'Wolfie', role: 'Co-Founder', color: 'border-red-500', image: 'https://static.readdy.ai/image/631821f386b34ef1da569fb8b8560804/672f8ec3bd6d0cf5722a03a6d9cb4bcb.png', path: '/wolfie' },
    { name: 'Ruby', role: 'Co-Founder', color: 'border-blue-600', image: 'https://static.readdy.ai/image/631821f386b34ef1da569fb8b8560804/452c66b1ae4903d7c0936d1443cd6f3f.png', path: '/ruby' }
  ];

  const features = [
    { icon: 'ri-leaf-fill', text: 'Eco-Friendly Products', color: 'text-green-500' },
    { icon: 'ri-star-fill', text: '5-Star Service', color: 'text-yellow-400' },
    { icon: 'ri-team-fill', text: 'Kid Entrepreneurs', color: 'text-blue-500' },
    { icon: 'ri-home-smile-fill', text: 'Community Care', color: 'text-red-500' }
  ];

  return (
    <section id="about" className="py-16 sm:py-24 bg-gradient-to-br from-red-100 via-yellow-100 to-green-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-black text-yellow-500 mb-6" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
            About Us
          </h2>
          <p className="text-2xl sm:text-3xl text-gray-800 font-bold mb-8" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
            We're kids with a mission to keep our neighborhood clean!
          </p>
        </motion.div>

        {/* Mission Statement & Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left side - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-blue-400">
              <h3 className="text-2xl font-bold text-blue-600 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Our Mission
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong className="text-blue-600">Can-do Crew</strong> is a kid-funded and operated business that brings professional trash can cleaning to your neighborhood. We believe in hard work, responsibility, and community service.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-green-400">
              <h3 className="text-2xl font-bold text-green-600 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                What We Do
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                As young entrepreneurs, we are trained to provide <span className="font-bold text-red-500">top-quality cleaning services</span> while learning valuable business and life skills. Every job teaches us teamwork, customer service, and the importance of a clean environment.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-yellow-400">
              <h3 className="text-2xl font-bold text-yellow-600 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Eco-Friendly Promise
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                We use <span className="font-bold text-green-500">eco-friendly products</span> that are safe for your family, pets, and the planet. Our mission is to make your neighborhood cleaner, one trash can at a time!
              </p>
            </div>
          </motion.div>

          {/* Right side - Images */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-[600px]"
          >
            <div className="absolute top-0 left-0 w-72 h-64 rounded-3xl overflow-hidden transform rotate-3 shadow-xl border-8 border-white">
              <img
                src="https://public.readdy.ai/ai/img_res/00dfd8b42006c21491d0e40cddbc3ace.jpg"
                alt="Kids cleaning trash cans"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute top-20 right-0 w-80 h-72 rounded-3xl overflow-hidden transform -rotate-2 shadow-xl border-8 border-white">
              <img
                src="https://readdy.ai/api/search-image?query=young%20boy%20and%20girl%20in%20matching%20blue%20overalls%20and%20red%20caps%20standing%20next%20to%20sparkling%20clean%20blue%20trash%20bins%2C%20giving%20thumbs%20up%2C%20proud%20smiling%20faces%2C%20suburban%20house%20background%2C%20bright%20sunny%20day%2C%20professional%20kid-friendly%20service%20aesthetic%20with%20primary%20colors%2C%20clean%20simple%20background%20showcasing%20the%20cleaned%20bins&width=450&height=400&seq=about2&orientation=squarish"
                alt="Team with clean bins"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute bottom-0 left-12 w-76 h-68 rounded-3xl overflow-hidden transform rotate-2 shadow-xl border-8 border-white">
              <img
                src="https://readdy.ai/api/search-image?query=group%20of%20diverse%20kids%20aged%20eight%20to%20twelve%20wearing%20colorful%20caps%20and%20t-shirts%20in%20primary%20colors%2C%20holding%20cleaning%20supplies%20and%20brushes%2C%20standing%20together%20as%20a%20team%20in%20front%20of%20a%20neighborhood%20street%2C%20happy%20and%20energetic%20expressions%2C%20professional%20cleaning%20crew%20photo%20with%20bright%20cheerful%20atmosphere%20and%20simple%20clean%20suburban%20background&width=450&height=400&seq=about3&orientation=squarish"
                alt="Can-do Crew team"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center border-4 border-gray-100 hover:border-blue-300 transition-all cursor-pointer"
            >
              <i className={`${feature.icon} text-4xl ${feature.color} mb-3 block`}></i>
              <p className="font-bold text-gray-800 text-sm" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                {feature.text}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-blue-400">
            <h3 className="text-4xl md:text-5xl font-black text-blue-600 mb-8" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Meet the Can-do Crew!
            </h3>
            <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto">
              Get to know the young entrepreneurs behind our cleaning service!
            </p>

            <div className="flex items-center justify-center gap-8 md:gap-16 flex-wrap">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.4 }}
                  whileHover={{ scale: 1.1, y: -10 }}
                  onClick={() => navigate(member.path)}
                  className="text-center cursor-pointer group"
                >
                  <div className={`w-40 h-40 rounded-full overflow-hidden border-8 ${member.color} shadow-xl mb-4 group-hover:shadow-2xl transition-all`}>
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-[center_30%]"
                    />
                  </div>
                  <p className="text-2xl font-bold text-gray-800 mb-1" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                    {member.name}
                  </p>
                  <p className="text-lg text-gray-600 font-semibold">
                    {member.role}
                  </p>
                  <p className="text-sm text-blue-500 font-bold mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to learn more!
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
